import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Modal,
  Descriptions,
  Tabs,
  Progress,
  Row,
  Col,
  Statistic,
  List,
  Avatar,
  message,
  Popconfirm,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  SettingOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const MemoryManagement: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  // 处理函数
  const handleExport = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要导出的记录');
      return;
    }
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success(`已导出 ${selectedRowKeys.length} 条记忆记录`);
    } catch (error) {
      message.error('导出失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的记录');
      return;
    }
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success(`已删除 ${selectedRowKeys.length} 条记忆记录`);
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('批量删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    message.loading('正在刷新记忆数据...', 1);
    setTimeout(() => {
      setLoading(false);
      message.success('记忆数据刷新成功！');
    }, 1500);
  };

  const handleViewMemory = (record: any) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const handleDeleteMemory = (record: any) => {
    message.loading('正在删除记忆...', 1);
    setTimeout(() => {
      message.success(`已删除用户 ${record.username} 的记忆数据`);
    }, 1000);
  };

  const handleSaveSettings = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success('记忆配置保存成功！');
      setConfigModalVisible(false);
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 模拟对话记录数据
  const conversationData = [
    {
      id: 1,
      userId: 'user_001',
      username: '张三',
      sessionId: 'session_001',
      messageCount: 15,
      duration: '8分钟',
      startTime: '2025-10-23 14:30:00',
      endTime: '2025-10-23 14:38:00',
      agent: '游戏介绍Bot',
      summary: '用户询问老虎机游戏规则，了解了基本玩法和投注方式',
      keywords: ['老虎机', '游戏规则', '投注'],
      status: 'completed',
      tokenUsage: 1250,
      compressionRate: 0.75,
    },
    {
      id: 2,
      userId: 'user_002',
      username: '李四',
      sessionId: 'session_002',
      messageCount: 8,
      duration: '5分钟',
      startTime: '2025-10-23 14:25:00',
      endTime: '2025-10-23 14:30:00',
      agent: '客服Bot',
      summary: '用户咨询充值问题，已解决充值未到账问题',
      keywords: ['充值', '问题', '解决'],
      status: 'completed',
      tokenUsage: 680,
      compressionRate: 0.82,
    },
    {
      id: 3,
      userId: 'user_003',
      username: '王五',
      sessionId: 'session_003',
      messageCount: 22,
      duration: '12分钟',
      startTime: '2025-10-23 14:20:00',
      endTime: '2025-10-23 14:32:00',
      agent: '气氛活跃Bot',
      summary: '用户参与游戏互动，气氛活跃，用户满意度较高',
      keywords: ['游戏', '互动', '满意'],
      status: 'processing',
      tokenUsage: 1890,
      compressionRate: 0.68,
    },
  ];

  const columns = [
    {
      title: '会话ID',
      dataIndex: 'sessionId',
      key: 'sessionId',
      width: 120,
    },
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      width: 100,
      render: (text: string, record: any) => (
        <Space>
          <Avatar size="small">{text.charAt(0)}</Avatar>
          {text}
        </Space>
      ),
    },
    {
      title: '智能体',
      dataIndex: 'agent',
      key: 'agent',
      width: 120,
      render: (agent: string) => (
        <Tag color="blue">{agent}</Tag>
      ),
    },
    {
      title: '消息数',
      dataIndex: 'messageCount',
      key: 'messageCount',
      width: 80,
    },
    {
      title: '时长',
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
    },
    {
      title: '开始时间',
      dataIndex: 'startTime',
      key: 'startTime',
      width: 150,
    },
    {
      title: '记忆摘要',
      dataIndex: 'summary',
      key: 'summary',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'completed' ? 'green' : 'blue'}>
          {status === 'completed' ? '已完成' : '进行中'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedRecord(record);
              setDetailModalVisible(true);
            }}
          >
            查看
          </Button>
          <Button type="link" icon={<EditOutlined />}>
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这条记忆吗？"
            onConfirm={() => handleDeleteMemory(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" icon={<DeleteOutlined />} danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const memoryStats = [
    { title: '总对话数', value: 12580, icon: <DatabaseOutlined /> },
    { title: '今日新增', value: 324, icon: <ClockCircleOutlined /> },
    { title: '平均Token', value: 1250, icon: <DatabaseOutlined /> },
    { title: '压缩率', value: '75%', icon: <DatabaseOutlined /> },
  ];

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>记忆管理</h1>
          <p>管理所有聊天记录，实现短期/长期记忆管理，优化Token使用</p>
        </div>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={handleRefresh}
          loading={loading}
          type="primary"
        >
          刷新
        </Button>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {memoryStats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <div className="stats-card">
                <div style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }}>
                  {stat.icon}
                </div>
                <div className="stats-number">{stat.value}</div>
                <div className="stats-label">{stat.title}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 搜索和操作区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Input
              placeholder="搜索用户、会话ID或关键词"
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select placeholder="选择智能体" allowClear style={{ width: '100%' }}>
              <Option value="气氛活跃Bot">气氛活跃Bot</Option>
              <Option value="产品介绍Bot">产品介绍Bot</Option>
              <Option value="客服Bot">客服Bot</Option>
              <Option value="游戏介绍Bot">游戏介绍Bot</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <RangePicker style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={4}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button icon={<SettingOutlined />} onClick={() => setConfigModalVisible(true)}>
                配置
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 批量操作 */}
      {selectedRowKeys.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <Space>
            <span>已选择 {selectedRowKeys.length} 条记录</span>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>
            <Button icon={<DeleteOutlined />} danger onClick={handleBatchDelete}>删除</Button>
          </Space>
        </Card>
      )}

      {/* 对话记录表格 */}
      <Card>
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={conversationData}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{
            total: conversationData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      {/* 详情模态框 */}
      <Modal
        title="对话详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
      >
        {selectedRecord && (
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="基本信息" key="1">
              <Descriptions column={2}>
                <Descriptions.Item label="会话ID">{selectedRecord.sessionId}</Descriptions.Item>
                <Descriptions.Item label="用户">{selectedRecord.username}</Descriptions.Item>
                <Descriptions.Item label="智能体">{selectedRecord.agent}</Descriptions.Item>
                <Descriptions.Item label="消息数">{selectedRecord.messageCount}</Descriptions.Item>
                <Descriptions.Item label="时长">{selectedRecord.duration}</Descriptions.Item>
                <Descriptions.Item label="状态">
                  <Tag color={selectedRecord.status === 'completed' ? 'green' : 'blue'}>
                    {selectedRecord.status === 'completed' ? '已完成' : '进行中'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Token使用">{selectedRecord.tokenUsage}</Descriptions.Item>
                <Descriptions.Item label="压缩率">{selectedRecord.compressionRate * 100}%</Descriptions.Item>
              </Descriptions>
            </Tabs.TabPane>
            <Tabs.TabPane tab="记忆摘要" key="2">
              <div style={{ marginBottom: 16 }}>
                <h4>摘要内容</h4>
                <p>{selectedRecord.summary}</p>
              </div>
              <div>
                <h4>关键词</h4>
                <Space wrap>
                  {selectedRecord.keywords.map((keyword: string, index: number) => (
                    <Tag key={index}>{keyword}</Tag>
                  ))}
                </Space>
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane tab="对话记录" key="3">
              <List
                size="small"
                dataSource={[
                  { user: '张三', message: '你好，我想了解一下游戏规则', time: '14:30:15' },
                  { user: '游戏介绍Bot', message: '您好！很高兴为您介绍游戏规则...', time: '14:30:18' },
                  { user: '张三', message: '老虎机怎么玩？', time: '14:30:45' },
                  { user: '游戏介绍Bot', message: '老虎机是一款经典的游戏...', time: '14:30:48' },
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar size="small">{item.user.charAt(0)}</Avatar>}
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>{item.user}</span>
                          <span style={{ fontSize: 12, color: '#999' }}>{item.time}</span>
                        </div>
                      }
                      description={item.message}
                    />
                  </List.Item>
                )}
              />
            </Tabs.TabPane>
          </Tabs>
        )}
      </Modal>

      {/* 配置模态框 */}
      <Modal
        title="记忆管理配置"
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setConfigModalVisible(false)}>
            取消
          </Button>,
          <Button key="save" type="primary">
            保存
          </Button>,
        ]}
      >
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="短期记忆" key="1">
            <div style={{ marginBottom: 16 }}>
              <label>保留轮数：</label>
              <Input type="number" defaultValue={10} style={{ width: 100, marginLeft: 8 }} />
              <span style={{ marginLeft: 8 }}>轮</span>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label>时间窗口：</label>
              <Input type="number" defaultValue={30} style={{ width: 100, marginLeft: 8 }} />
              <span style={{ marginLeft: 8 }}>分钟</span>
            </div>
            <div>
              <label>自动清理：</label>
              <Select defaultValue="enabled" style={{ width: 120, marginLeft: 8 }}>
                <Option value="enabled">启用</Option>
                <Option value="disabled">禁用</Option>
              </Select>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="长期记忆" key="2">
            <div style={{ marginBottom: 16 }}>
              <label>摘要长度：</label>
              <Input type="number" defaultValue={200} style={{ width: 100, marginLeft: 8 }} />
              <span style={{ marginLeft: 8 }}>字符</span>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label>关键词数量：</label>
              <Input type="number" defaultValue={5} style={{ width: 100, marginLeft: 8 }} />
              <span style={{ marginLeft: 8 }}>个</span>
            </div>
            <div>
              <label>存储策略：</label>
              <Select defaultValue="auto" style={{ width: 120, marginLeft: 8 }}>
                <Option value="auto">自动存储</Option>
                <Option value="manual">手动存储</Option>
              </Select>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Token优化" key="3">
            <div style={{ marginBottom: 16 }}>
              <label>目标压缩率：</label>
              <Progress percent={75} style={{ width: 200, marginLeft: 8 }} />
              <span style={{ marginLeft: 8 }}>75%</span>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label>优化策略：</label>
              <Select defaultValue="aggressive" style={{ width: 150, marginLeft: 8 }}>
                <Option value="conservative">保守</Option>
                <Option value="balanced">平衡</Option>
                <Option value="aggressive">激进</Option>
              </Select>
            </div>
            <div>
              <label>过滤规则：</label>
              <TextArea
                rows={3}
                placeholder="输入过滤规则..."
                defaultValue="过滤重复内容\n过滤无意义对话\n保留关键信息"
              />
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Modal>
    </div>
  );
};

export default MemoryManagement;
